import { ContextStore, PageStore, Store } from 'bernie-plugin-mobx';
import { SerializedData } from 'bernie-core';
import { CarSearchV3, ContextInput } from '../../src/common/__generated__/api';
import { getBexApiContext } from 'bernie-context';
import { action, computed, observable } from 'mobx';
import {
  formatTimeForTwelveHourFormat,
  IFormatedDateTime,
  parseTimeFromURL,
  resolveDate,
  resolveTime
} from 'helpers/date-time-resolver';
import { AuthenticationState } from 'common/__generated__/api/types';
import { selectedCCfromSF } from 'utils/store-front-model';
import { getStaticConfig } from 'config/static-config';
import { MultiItemStore } from 'stores/multi-item-store';
import * as browserStorage from 'utils/localStorage';
import { USE_REWARDS_KEY, UNLOCKED_CAR_SALES_LS_KEY } from 'src/utils/localStorageKeys';

export interface ISearchQuery {
  pickUpLoc?: string;
  pickupRegion?: string;
  dropLoc?: string;
  dropRegion?: string;
  pickupCode?: string;
  dropCode?: string;
  pickUpExactLoc?: string;
  dropExactLoc?: string;
  pickUpDate?: string;
  dropDate?: string;
  pickUpTime?: string;
  dropTime?: string;
  categories?: object;
  oloc?: string;
  aarpcr?: string;
  useRewards?: string;
  corporateDiscountCode?: string;
  promoCode?: string;
  rateCode?: string;
  loyaltyProgramCode?: string;
  vend?: string;
  carOfferToken?: string;
  metaOfferPrice?: string;
  age?: string;
  rfrr?: string;
  pickUpLat?: string;
  pickUpLong?: string;
  dropLat?: string;
  dropLong?: string;
  searchId?: string;
}

// map for updating query from on user-interaction (Keys are used in across application, values are for query params mapping)
export enum changeSearchParams {
  pickUpLoc = 'locn',
  dropLoc = 'loc2',
  pickUpDate = 'date1',
  dropDate = 'date2',
  pickUpTime = 'time1',
  dropTime = 'time2',
  pickupCode = 'pickupCode',
  dropCode = 'dropCode',
  pickUpExactLoc = 'exlocn',
  dropExactLoc = 'exloc2',
  sort = 'selSort',
  pickupRegion = 'dpln',
  dropRegion = 'drid1',
  originalRegionId = 'orid',
  originalSearchTerm = 'oloc',
  aarpcr = 'aarpcr',
  useRewards = 'useRewards',
  corporateDiscountCode = 'cdnu',
  promoCode = 'cpcd',
  rateCode = 'rtcd',
  loyaltyProgramCode = 'odcd',
  carOfferToken = 'piid',
  metaOfferPrice = 'totalprice',
  age = 'age',
  pickUpLat = 'olat',
  pickUpLong = 'olon',
  dropLat = 'dlat',
  dropLong = 'dlon',
  misId = 'misId',
  packageType = 'packageType',
  searchProduct = 'searchProduct',
  rfrr = 'rfrr'
}

// used for parsing available query params to the values, used across application
export enum changeSearchParamsMap {
  locn = 'pickUpLoc',
  loc2 = 'dropLoc',
  date1 = 'pickUpDate',
  date2 = 'dropDate',
  time1 = 'pickUpTime',
  time2 = 'dropTime',
  pickupCode = 'pickupCode',
  dropCode = 'dropCode',
  dpln = 'pickupRegion',
  drid1 = 'dropRegion',
  exlocn = 'pickUpExactLoc',
  exloc2 = 'dropExactLoc',
  orid = 'originalRegionId',
  oloc = 'originalSearchTerm',
  aarpcr = 'aarpcr',
  useRewards = 'useRewards',
  cdnu = 'corporateDiscountCode',
  cpcd = 'promoCode',
  rtcd = 'rateCode',
  odcd = 'loyaltyProgramCode',
  vend = 'selVen',
  kind = 'selCC',
  piid = 'carOfferToken',
  totalprice = 'metaOfferPrice',
  age = 'age',
  rfrr = 'rfrr',
  olat = 'pickUpLat',
  olon = 'pickUpLong',
  dlat = 'dropLat',
  dlon = 'dropLong',
  misId = 'misId',
  packageType = 'packageType',
  searchProduct = 'searchProduct'
}

export interface Selections {
  id: string;
  value: string;
}

export const searchDetailCombinedParams = [
  'pickUpLoc',
  'dropLoc',
  'pickUpDate',
  'dropDate',
  'pickUpTime',
  'dropTime',
  'pickupRegion',
  'dropRegion'
];

export class SearchCriteriaStore extends Store {
  public context: ContextInput;
  private page: PageStore;
  private contextStore: ContextStore;
  private multiItemStore: MultiItemStore;
  public formattedDateTime: IFormatedDateTime = {} as IFormatedDateTime;
  public static NUM_OF_DAYS_BOOKABLE_IN_ADVANCE = 329;
  public static MIDNIGHT_BOOKING_OFFSET_IN_HOURS = 4;
  public searchQueryParams = [...searchDetailCombinedParams, 'sort', 'category', 'rfrr'];
  private dateTimeKeys = {
    pickUpDate : 'pickUpDate',
    dropDate   : 'dropDate',
    pickUpTime : 'pickUpTime',
    dropTime   : 'dropTime'
  };

  @observable
  public searchQuery: ISearchQuery = {};

  // To change the implemenation once Change-search-form validation will be applied
  @observable
  public formSubmitted: boolean = true;

  @observable
  public carSalesUnlocked: boolean = false;

  @observable
  public showMoreSelected = false;

  @observable
  public isSortAndFilterChanging = false;

  @observable
  public isLoyaltyToggleChanging = false;

  @observable
  public pillRemoveIndex?: number = null;

  //boolean value that helps with tracking searchId
  @observable
  public isNewSearch = true;

  /**
   * savedSearchId is the requestId of the first search request that a user makes.
   * Used when sort/filter/pagination request is made.
   */
  @observable
  public savedSearchId = '';

  // by-default points are applied
  public useRewards = true;

  public setContext(context: ContextStore) {
    this.context = getBexApiContext(context);
    this.contextStore = context;
  }

  public setPage(page: PageStore) {
    this.page = page;
  }

  public setMultiItemStore(multiItemStore: MultiItemStore) {
    this.multiItemStore = multiItemStore;
  }

  public setSavedSearchId = (searchId: string) => {
    this.savedSearchId = searchId;
  }

  public getSavedSearchId = (): string => {
    return this.savedSearchId;
  }

  @action
  public setSearchQuery = (query: ISearchQuery) => {
    let isQueryParamDiffent = false;

    const isPreviousQuery = !!query['prevQuery'] && Object.keys(query['prevQuery']).length > 0;

    const { defaultPickupTime, defaultDropoffTime } = getStaticConfig(this.context.siteId).changeSearch;
    const unlockSalesKeyValue = browserStorage.get(UNLOCKED_CAR_SALES_LS_KEY);

    this.carSalesUnlocked = unlockSalesKeyValue ? unlockSalesKeyValue : this.carSalesUnlocked;

    if (!this.carSalesUnlocked && query['selEO']) {
      const dealsFilters = JSON.parse(decodeURIComponent(query['selEO']));

      if (dealsFilters.includes('SALES_DEAL')) {
        query['selEo'] = encodeURIComponent(JSON.stringify(dealsFilters.filter(deal => deal !== 'SALES_DEAL')));
      }
    }

    const searchQueryObj: ISearchQuery = {};
    const appliedFilterInPrevQuery =
      this.searchQuery['categories'] && Object.keys(this.searchQuery['categories']).length;
    searchQueryObj['categories'] = {};
    Object.keys(query).map(key => {
      if (!key.startsWith('pwa') && key !== 'notificationCenterDeleteDialog') {
        // omitting all dialogues
        const paramKey = changeSearchParamsMap[key] ? changeSearchParamsMap[key] : '';
        if (typeof query[key] === 'string') {
          if (
            key.startsWith('sel') ||
            key === 'orid' ||
            key === 'oloc' ||
            key === 'rdus' ||
            key === 'kind' ||
            key === 'vend' ||
            key === 'piid' ||
            key === 'totalprice' ||
            key === 'age' ||
            key === 'rfrr'
          ) {
            let selectionKeyName = key;
            if (key === 'piid') {
              selectionKeyName = paramKey; //deprecating the flow of name piid
            }
            searchQueryObj['categories'] = {
              ...searchQueryObj['categories'],
              [selectionKeyName] : decodeURIComponent(query[key])
            };
          } else if (paramKey) {
            searchQueryObj[paramKey] = decodeURIComponent(query[key]);
            // format params other than locations
            if (paramKey === this.dateTimeKeys[paramKey]) {
              this.setupDateParams(paramKey, searchQueryObj[paramKey]);
            }
          }
          if (!isQueryParamDiffent && isPreviousQuery) {
            isQueryParamDiffent = query[key] === query['prevQuery'][key] ? isQueryParamDiffent : true;
          }
        }
      }
    });

    if (!searchQueryObj.pickUpTime) {
      searchQueryObj.pickUpTime = defaultPickupTime;
    }

    if (!searchQueryObj.dropTime) {
      searchQueryObj.dropTime = defaultDropoffTime;
    }

    // during Filter-clear option
    isQueryParamDiffent =
      appliedFilterInPrevQuery !== Object.keys(searchQueryObj['categories']).length ? true : isQueryParamDiffent;
    
    if (!isPreviousQuery || isQueryParamDiffent) {
      this.searchQuery = { ...searchQueryObj };
    }

    return isQueryParamDiffent;
  };

  public setupDateParams(key: string, stringFormattedDate: string) {
    const { localDateFormat } = this.contextStore;
    switch (key) {
    case 'pickUpDate':
      this.formattedDateTime.formattedPickupDate = resolveDate(stringFormattedDate, localDateFormat);
      break;
    case 'dropDate':
      this.formattedDateTime.formattedDropDate = resolveDate(stringFormattedDate, localDateFormat);
      break;
    case 'pickUpTime':
      this.formattedDateTime.formattedPickupTime = resolveTime(
        formatTimeForTwelveHourFormat(parseTimeFromURL(stringFormattedDate))
      );
      break;
    case 'dropTime':
      this.formattedDateTime.formattedDropTime = resolveTime(
        formatTimeForTwelveHourFormat(parseTimeFromURL(stringFormattedDate))
      );
      break;
    }
  }

  public appliedSortAndFilterList = () => {
    const sortAndFilterList = this.searchQuery.categories && Object.keys(this.searchQuery.categories);
    const applyRewards = this.arePointsApplied();
    const discountCodeCriteria = this.getDiscountCodeCriteria();
    let appliedSnF = [];
    if (sortAndFilterList && sortAndFilterList.length) {
      const categories = { ...this.searchQuery.categories };
      sortAndFilterList.map(key => {
        // category contains array (with JSON.Stringify) OR string value from single Select
        if (categories[key].includes('[')) {
          const parsedfilterArrayList = categories[key].replace(/'/g, '"'); // signing-in changes the url and adds "'"
          const idsList = JSON.parse(parsedfilterArrayList).map(filterOptionId => {
            return {
              id    : key,
              value : filterOptionId
            };
          });
          appliedSnF = [...appliedSnF, ...idsList];
        } else {
          //page refresh handle, re-initialize this key to show offers from beginning
          if (key === 'selPageIndex' && !this.showMoreSelected) {
            categories[key] = '0';
          }
          if (key === 'kind' || key === 'vend') {
            appliedSnF = appliedSnF.concat(this.appliedStoreFrontFilters(key, categories[key]));
          } else {
            // filterOption from singleSelect
            appliedSnF.push({ id : key, value : categories[key] });
          }
        }
      });
    }

    // for loyalty Points. would be send on every call
    if (this.context.identity.authState === AuthenticationState.AUTHENTICATED) {
      appliedSnF.push({ id : 'useRewards', value : applyRewards ? 'ApplyRewardsPoints' : 'DoNotApplyRewardsPoints' });
    }

    if (discountCodeCriteria.length > 0) {
      appliedSnF = [...appliedSnF, ...discountCodeCriteria];
    }

    //To hotfix deals experiences for deals pages not passing rfrr params. TODO: This needs clean up later on
    if (
      document.referrer &&
      document.referrer.indexOf(document.location.host) > -1 &&
      document.referrer.indexOf('/cyber') > -1
    ) {
      appliedSnF.push({ id : 'rfrr', value : 'TG.LP.SrchWzd.Car' });
    }

    //set the searchId to savedId before sending request
    if (this.isNewSearch) {
      this.setSavedSearchId('');
      this.isNewSearch = false;
    }
    appliedSnF.push({ id : 'searchId', value : this.getSavedSearchId() });

    return appliedSnF;
  };

  /**
   * 'kind' and 'vend' are queryparams for CC and selVen respectively which generally passed by SF. So they will only remain for the first
   *  time. and later gets merged in selCC and selVen if user makes any changes in those filter category.
   *  New SF doesn't provide this feature so later these two conditions can be removed from here, once new SF would be rolled out.
   */
  public appliedStoreFrontFilters = (key: string, filterValue: string): Selection[] => {
    const appliedSnF = [];
    if (key === 'kind' && selectedCCfromSF[filterValue]) {
      appliedSnF.push({ id : changeSearchParamsMap[key], value : selectedCCfromSF[filterValue] });
    } else if (key === 'vend' && filterValue) {
      appliedSnF.push({ id : changeSearchParamsMap[key], value : filterValue });
    }

    return appliedSnF;
  };

  public arePointsApplied = () => {
    return this.searchQuery.useRewards === 'true' ||  ( browserStorage.get(USE_REWARDS_KEY) !== null ? browserStorage.get(USE_REWARDS_KEY)  : this.useRewards );
  };

  public getBooleansList = () => {
    const unlockSalesKeyValue = browserStorage.get(UNLOCKED_CAR_SALES_LS_KEY);
    const booleans = [
      {
        id    : 'SALES_UNLOCKED',
        value : unlockSalesKeyValue ? unlockSalesKeyValue : this.carSalesUnlocked
      }
    ];

    if (this.searchQuery.aarpcr && this.searchQuery.aarpcr === 'on') {
      booleans.push({ id : changeSearchParams.aarpcr, value : true });
    }

    return booleans;
  };

  @computed
  public get searchVariables(): CarSearchV3.Variables {
    const {
      formattedPickupDate: pickupDate,
      formattedPickupTime: pickUpTime,
      formattedDropDate: dropDate,
      formattedDropTime: dropTime
    } = this.formattedDateTime;
    const { dropRegion, pickupRegion, pickUpLoc, dropLoc, pickUpLat, pickUpLong, dropLat, dropLong, pickUpExactLoc, dropExactLoc } = this.searchQuery;
    this.formSubmitted = false;
    const pickupRegionId = pickupRegion && Number(pickupRegion) !== 0 ? pickupRegion : undefined;
    const dropOffRegionId = dropRegion ? (dropRegion && Number(dropRegion) !== 0 ? dropRegion : undefined) : undefined;
    const dropOffSearchTerm = dropLoc || undefined;
    const pickUpCoordinates = (!pickupRegionId && pickUpLat && pickUpLong) ? { latitude : Number(pickUpLat), longitude : Number(pickUpLong) } : undefined;
    const dropOffCoordinates = (!dropOffRegionId && dropLat && dropLong) ? { latitude : Number(dropLat), longitude : Number(dropLong) } : undefined;
    const isPickUpExactLoc = !!pickUpCoordinates || Boolean(pickUpExactLoc);
    const isDropExactLoc = !!dropOffCoordinates || Boolean(dropExactLoc);

    return {
      context                  : this.context,
      primaryCarSearchCriteria : {
        pickUpLocation :
        this.multiItemStore.isMultiItem
          ? {
            airportCode : pickUpLoc || undefined,
            regionId    : pickupRegionId,
            coordinates : pickUpCoordinates
          } : {
            searchTerm            : pickUpLoc || undefined,
            regionId              : pickupRegionId,
            coordinates           : pickUpCoordinates,
            isExactLocationSearch : isPickUpExactLoc
          },
        dropOffLocation :
        this.multiItemStore.isMultiItem
          ? {
            airportCode : dropLoc || undefined,
            regionId    : dropOffRegionId,
            coordinates : dropOffCoordinates
          } :
          dropOffRegionId || dropOffSearchTerm || dropOffCoordinates
            ? {
              searchTerm            : dropOffSearchTerm,
              regionId              : dropOffRegionId,
              coordinates           : dropOffCoordinates,
              isExactLocationSearch : isDropExactLoc
            }
            : undefined,
        pickUpDateTime : {
          day    : pickupDate && pickupDate.day ? pickupDate.day : undefined,
          month  : pickupDate && pickupDate.month ? pickupDate.month : undefined,
          year   : pickupDate && pickupDate.year ? pickupDate.year : undefined,
          hour   : pickUpTime && pickUpTime.hour !== undefined ? pickUpTime.hour : 10,
          minute : pickUpTime && pickUpTime.minute !== undefined ? pickUpTime.minute : 30,
          second : 0
        },
        dropOffDateTime : {
          day    : dropDate && dropDate.day ? dropDate.day : undefined,
          month  : dropDate && dropDate.month ? dropDate.month : undefined,
          year   : dropDate && dropDate.year ? dropDate.year : undefined,
          hour   : dropTime && dropTime.hour !== undefined ? dropTime.hour : 10,
          minute : dropTime && dropTime.minute !== undefined ? dropTime.minute : 30,
          second : 0
        }
      },
      secondaryCriteria : {
        booleans   : this.getBooleansList(),
        selections : this.appliedSortAndFilterList()
      },
      shoppingContext : this.multiItemStore.getShoppingContext()
    };
  }

  public hydrate(data: SerializedData): void {
    this.context = { ...data.context };
    this.searchQuery = { ...data.searchQuery };
    this.formattedDateTime = { ...data.formattedDateTime };
  }

  private getDiscountCodeCriteria = () => {
    const discountedCriteria = [];
    if (this.searchQuery.corporateDiscountCode) {
      discountedCriteria.push({ id : 'corporateDiscountCode', value : this.searchQuery.corporateDiscountCode });
    }
    if (this.searchQuery.promoCode) {
      discountedCriteria.push({ id : 'promoCode', value : this.searchQuery.promoCode });
    }
    if (this.searchQuery.rateCode) {
      discountedCriteria.push({ id : 'rateCode', value : this.searchQuery.rateCode });
    }
    if (this.searchQuery.loyaltyProgramCode) {
      discountedCriteria.push({ id : 'loyaltyProgramCode', value : this.searchQuery.loyaltyProgramCode });
    }

    return discountedCriteria;
  };
}
